import { Search } from "lucide-react";
import { useState } from "react";

import { ALL_FILTER, FilterSelect } from "@/components/filter-select";
import { PaginationFooter } from "@/components/pagination-footer";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type AdminUserSummary, useAdminActivity } from "@/lib/api";

import { ActivityTable, type SortDir } from "./activity-table";

interface Props {
  users: AdminUserSummary[];
  actions: string[];
}

const PAGE_SIZE = 25;

/**
 * Interactive activity browser. All filtering, searching, sorting, and
 * pagination is server-side — every control change re-runs `useAdminActivity`
 * with new params, which retriggers the backend query.
 */
export function ActivityExplorer({ users, actions }: Props) {
  const [search, setSearch] = useState("");
  const [action, setAction] = useState(ALL_FILTER);
  const [userId, setUserId] = useState(ALL_FILTER);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  // Debounce the search box so we don't query on every keystroke; jump back to
  // the first page when the effective search changes.
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(1));

  const activityQuery = useAdminActivity({
    search: debouncedSearch || undefined,
    action: action === ALL_FILTER ? undefined : action,
    userId: userId === ALL_FILTER ? undefined : userId,
    sortBy,
    sortDir,
    page,
    pageSize: PAGE_SIZE,
  });

  const data = activityQuery.data ?? { items: [], total: 0, page: 1, page_size: PAGE_SIZE };
  const loading = activityQuery.isFetching;

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <FilterSelect
          value={userId}
          onValueChange={(v) => {
            setUserId(v);
            setPage(1);
          }}
          allLabel="All users"
          options={users.map((u) => ({
            value: u.id,
            label: `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email,
          }))}
          className="w-full sm:w-48"
        />
        <FilterSelect
          value={action}
          onValueChange={(v) => {
            setAction(v);
            setPage(1);
          }}
          allLabel="All actions"
          options={actions.map((a) => ({ value: a, label: a }))}
          className="w-full sm:w-44"
        />
      </div>

      <ActivityTable
        entries={data.items}
        searchTerm={debouncedSearch}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        loading={loading}
      />

      <PaginationFooter
        page={data.page}
        pageSize={PAGE_SIZE}
        total={data.total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
